<template>
  <div class="laudo-popup-container">
    <!-- Botão de teste temporário para debug -->
    <div v-if="!paciente" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
      <button @click="testarMascaras" class="px-4 py-2 bg-blue-600 text-white rounded-md text-sm">
        Testar Máscaras
      </button>
    </div>
    
    <!-- Renderizar o LaudoModal diretamente -->
    <LaudoModal 
      :isVisible="true" 
      :paciente="paciente" 
      :modo-painel="false"
      :modo-popup="true"
      @close="fecharPopup"
      @laudo-salvo="onLaudoSalvo"
      @laudo-finalizado="onLaudoFinalizado"
      @laudo-revisado="onLaudoRevisado"
      @adendo-adicionado="onAdendoAdicionado"
      @laudo-assinado="onLaudoAssinado"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import LaudoModal from '@/components/LaudoModal.vue';
import medicalReportsService from '@/services/medicalReportsService';

// Estado reativo
const paciente = ref(null);

// Flag para evitar envio duplicado de mensagens
let popupClosedMessageSent = false;

// Função para carregar dados do paciente via postMessage
const carregarDadosPaciente = () => {
  console.log('LaudoPopup: Iniciando carregamento de dados do paciente...');
  
  // Aguardar dados do paciente via postMessage da janela pai
  const timeout = setTimeout(() => {
    console.warn('LaudoPopup: Timeout - dados do paciente não foram recebidos');
  }, 5000); // 5 segundos de timeout

  // Listener para receber dados do paciente
  const handleMessage = (event) => {
    console.log('LaudoPopup: Mensagem recebida:', event.data);
    if (event.origin !== window.location.origin) return;
    
    if (event.data.type === 'PACIENTE_DATA') {
      clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
      
      try {
        const pacienteData = event.data.data;
        
        if (!pacienteData || !pacienteData.nome) {
          throw new Error('Dados do paciente inválidos ou incompletos');
        }

        // Validar se os dados essenciais estão presentes
        if (!pacienteData.numAcesso) {
          throw new Error('Número de acesso do paciente não encontrado');
        }

        // Usar dados recebidos diretamente
        paciente.value = pacienteData;
        console.log('LaudoPopup: Dados do paciente recebidos:', paciente.value);
        
      } catch (error) {
        console.error('Erro ao processar dados do paciente:', error);
      }
    } else if (event.data.type === 'PACIENTE_DATA_ERROR') {
      clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
      console.error('Erro recebido da janela pai:', event.data.error);
    }
  };

  window.addEventListener('message', handleMessage);

  // Solicitar dados do paciente à janela pai
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'REQUEST_PACIENTE_DATA'
      }, window.location.origin);
    } else {
      console.error('Janela pai não encontrada ou fechada');
    }
  } catch (error) {
    console.error('Erro ao solicitar dados do paciente:', error);
  }
};

// Função para fechar a popup
const fecharPopup = () => {
  console.log('LaudoPopup: Fechando popup via botão X...');
  
  // Marcar que a mensagem foi enviada para evitar duplicação
  popupClosedMessageSent = true;
  
  // Notificar a janela pai que a popup foi fechada
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_POPUP_CLOSED'
      }, window.location.origin);
      console.log('LaudoPopup: Mensagem de fechamento enviada com sucesso');
    } else {
      console.log('LaudoPopup: Janela pai não está disponível para enviar mensagem');
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de fechamento:', error);
  }
  
  // Aguardar um pouco antes de fechar para garantir que a mensagem seja enviada
  setTimeout(() => {
    try {
      window.close();
    } catch (error) {
      console.error('LaudoPopup: Erro ao fechar janela:', error);
    }
  }, 100);
};

// Event handlers do LaudoModal
const onLaudoSalvo = (laudo) => {
  console.log('LaudoPopup: Laudo salvo:', laudo);
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_SAVED',
        data: laudo
      }, window.location.origin);
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de laudo salvo:', error);
  }
};

const onLaudoFinalizado = (laudo) => {
  console.log('LaudoPopup: Laudo finalizado:', laudo);
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_FINALIZED',
        data: laudo
      }, window.location.origin);
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de laudo finalizado:', error);
  }
};

const onLaudoRevisado = (laudo) => {
  console.log('LaudoPopup: Laudo revisado:', laudo);
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_REVIEWED',
        data: laudo
      }, window.location.origin);
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de laudo revisado:', error);
  }
};

const onAdendoAdicionado = (laudo) => {
  console.log('LaudoPopup: Adendo adicionado:', laudo);
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_ADDENDA_ADDED',
        data: laudo
      }, window.location.origin);
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de adendo adicionado:', error);
  }
};

const onLaudoAssinado = (laudo) => {
  console.log('LaudoPopup: Laudo assinado:', laudo);
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'LAUDO_SIGNED',
        data: laudo
      }, window.location.origin);
    }
  } catch (error) {
    console.error('LaudoPopup: Erro ao enviar mensagem de laudo assinado:', error);
  }
};

// Função de teste para máscaras
const testarMascaras = async () => {
  console.log('LaudoPopup: Testando carregamento de máscaras...');
  try {
    const response = await medicalReportsService.listarMascaras();
    console.log('LaudoPopup: Resposta do teste de máscaras:', response);
    console.log('LaudoPopup: Dados das máscaras:', response.data);
    alert(`Máscaras encontradas: ${response.data?.results?.length || response.data?.length || 0}`);
  } catch (error) {
    console.error('LaudoPopup: Erro no teste de máscaras:', error);
    console.error('LaudoPopup: Status do erro:', error.response?.status);
    console.error('LaudoPopup: Mensagem do erro:', error.response?.data);
    
    let errorMessage = `Erro ao carregar máscaras: ${error.message}`;
    if (error.response?.status === 403) {
      errorMessage += '\n\nProblema de permissões - verifique se o usuário tem permissão para listar máscaras';
    } else if (error.response?.status === 404) {
      errorMessage += '\n\nEndpoint não encontrado';
    }
    
    alert(errorMessage);
  }
};

// Carregar dados quando o componente for montado
onMounted(() => {
  console.log('LaudoPopup: Componente montado, iniciando carregamento...');
  console.log('LaudoPopup: Modo popup ativado - máscaras devem ser carregadas automaticamente');
  
  // Teste de comunicação
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'TEST_COMMUNICATION',
        message: 'LaudoPopup está pronto para receber dados'
      }, window.location.origin);
    }
  } catch (error) {
    console.error('Erro ao enviar teste de comunicação:', error);
  }
  
  carregarDadosPaciente();
  
  // Listener para detectar quando a popup é fechada
  const handleBeforeUnload = () => {
    console.log('LaudoPopup: beforeunload disparado...');
    
    // Evitar envio duplicado de mensagens
    if (popupClosedMessageSent) {
      console.log('LaudoPopup: Mensagem de fechamento já foi enviada, ignorando...');
      return;
    }
    
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({
          type: 'LAUDO_POPUP_CLOSED',
          data: { paciente: paciente.value }
        }, window.location.origin);
        popupClosedMessageSent = true;
        console.log('LaudoPopup: Mensagem de fechamento enviada via beforeunload');
      } else {
        console.log('LaudoPopup: Janela pai não está disponível para enviar mensagem via beforeunload');
      }
    } catch (error) {
      console.error('LaudoPopup: Erro ao enviar mensagem de fechamento via beforeunload:', error);
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Cleanup quando o componente for desmontado
  onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });
});
</script>

<style scoped>
.laudo-popup-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

/* 
  Sobrescrevemos os estilos do LaudoModal para que ele se comporte como 
  o conteúdo principal da página, em vez de um modal sobreposto.
*/
:deep(.fixed.inset-0:not(.modal-assinatura)) {
  position: static !important;
  display: flex;
  flex: 1;
  background-color: transparent !important;
}

:deep(.bg-white.dark\\:bg-gray-800.rounded-lg.shadow-lg) {
  width: 100% !important;
  height: 100% !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}
</style> 